import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import "@maersk-global/mds-components-core/mc-toast";
import "@maersk-global/mds-components-core/mc-notification";
import '@maersk-global/mds-foundations/css/foundations.css';
import { AuthService } from "../../src/services/auth-service";

@customElement("fleet-session-expire-popup")
export class FleetSessionExpirePopup extends LitElement {
    @property({ type: Boolean }) _isSessionModalVisible = false;
    @property({ type: Number }) _minutesRemaining = 5;  // Track minutes
    @property({ type: Number }) _secondsRemaining = 0;  // Track seconds

    private timerId: number | null = null;
    private expiryCheckId: number | null = null; // Separate timer for checking expiry

    connectedCallback() {
        super.connectedCallback();
        this.startExpiryCheck();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.stopExpiryCheck();
    }

    private startExpiryCheck() {
        this.expiryCheckId = window.setInterval(() => {
            const expiry = localStorage.getItem((<any>window)["appenv"].loginInfo?.expiryDate);
            if (!expiry) return;

            const expiryTime = new Date(Number(expiry)).getTime();
            const now = new Date().getTime();
            const timeRemaining = expiryTime - now;

            const minutesRemaining = Math.floor(timeRemaining / 60000); // Convert milliseconds to minutes
            const secondsRemaining = Math.floor((timeRemaining % 60000) / 1000); // Get the remaining seconds

            // Show the popup 5 minutes (300,000 milliseconds) before expiry and update countdown
            if (timeRemaining <= 300000 && timeRemaining > 0) {
                this._isSessionModalVisible = true;
                this._minutesRemaining = minutesRemaining;
                this._secondsRemaining = secondsRemaining;
                this.startCountdown(timeRemaining);  // Start countdown when 5 minutes remaining
            }

            // If the session has already expired
            if (timeRemaining < 0) {
                AuthService.dispatchEvent(new CustomEvent('login-required', { detail: { isRelogin: !!expiry } }));
                this.stopExpiryCheck(); // Stop checking
            }
        }, 1000);
    }

    private startCountdown(timeRemaining: number) {
        if (this.timerId) {
            clearInterval(this.timerId); // Clear any previous countdown timer
        }

        this.updateCountdown(timeRemaining);

        // Start a new countdown timer that updates every second
        this.timerId = window.setInterval(() => {
            const now = new Date().getTime();
            const expiry = localStorage.getItem((<any>window)["appenv"].loginInfo?.expiryDate);
            const expiryTime = new Date(Number(expiry)).getTime();
            const timeRemaining = expiryTime - now;

            if (timeRemaining > 0) {
                this.updateCountdown(timeRemaining); // Update countdown timer
            } else {
                clearInterval(this.timerId!); // Stop the timer if it reaches 0
                this._minutesRemaining = 0;
                this._secondsRemaining = 0;
            }

            this.requestUpdate();
        }, 1000);
    }

    private updateCountdown(timeRemaining: number) {
        // Calculate minutes and seconds
        this._minutesRemaining = Math.floor(timeRemaining / 60000); // Convert milliseconds to minutes
        this._secondsRemaining = Math.floor((timeRemaining % 60000) / 1000); // Get the remaining seconds

        this.requestUpdate();
    }

    private stopExpiryCheck() {
        if (this.expiryCheckId) {
            clearInterval(this.expiryCheckId);
            this.expiryCheckId = null;
        }
        if (this.timerId) {
            clearInterval(this.timerId);
            this.timerId = null;
        }
    }

    private _handleLogout() {
        AuthService.logOut?.();
    }

    render() {
        return html`
      <mc-toast
        appearance="warning"
        ?open=${this._isSessionModalVisible}
        width="auto"
        duration="50000000"
        position="top-center">
        <mc-notification
          style="width: 600px"
          fit="large"
          heading="System session will expire soon" 
          icon="exclamation-triangle">
          Due to security reasons, your session will expire in 
          ${this._minutesRemaining} min${this._minutesRemaining !== 1 ? 's' : ''} 
          and ${this._secondsRemaining} sec${this._secondsRemaining !== 1 ? 's' : ''} automatically. <br>
          Please click on the below button to re-login.
        <div style="margin-top: 10px">   
            <a class="mds-warning__text-color" @click="${this._handleLogout}">Continue</a>
        </div>
        </mc-notification>
      </mc-toast>
    `;
    }
}